import type { OnboardingTrainingTopic } from './OnboardingWizard.types';
import { validateOptionalCompanyTerminology } from './companyTerminologyValidation';
import { HEX_COLOR_RE } from '../BrandIdentity/brandingShared';
// CEE-867: website validation removed from onboarding step 1
// import { isValidWebsiteUrl } from '../../utils/websiteUrl';
import {
  // ONBOARDING_MAX_ADDITIONAL_CONTEXT_LENGTH,
  ONBOARDING_MAX_MONTH_SELECTIONS,
  ONBOARDING_MONTH_OPTIONS,
  getOnboardingMaxTrainingTopicsForPackage,
  ONBOARDING_TRAINING_PACKAGE_OPTIONS,
} from './onboardingConstants';

const COMPANY_NAME_MAX_LENGTH = 500;

const VALID_TRAINING_PACKAGES = new Set<string>(
  ONBOARDING_TRAINING_PACKAGE_OPTIONS.map((option) => option.value),
);

export function validateStep1Fields(): Record<string, never> {
  return {};
}

export function isStep1Valid(): boolean {
  return true;
}

export function validateStep2Fields(trainingPackage: string): {
  trainingPackage?: string;
} {
  const errors: { trainingPackage?: string } = {};

  if (trainingPackage.trim().length === 0 || !VALID_TRAINING_PACKAGES.has(trainingPackage)) {
    errors.trainingPackage = 'required';
  }

  return errors;
}

export function isStep2Valid(trainingPackage: string): boolean {
  return Object.keys(validateStep2Fields(trainingPackage)).length === 0;
}

const INDUSTRY_MAX_LENGTH = 100;

export function validateStep3Fields(
  companyName: string | null,
  industry: string,
  employeeCount: string,
  regions: string[],
  employeeTerminology: string,
  speakUpSolution: string,
): {
  companyName?: string;
  industry?: string;
  employeeCount?: string;
  regions?: string;
  employeeTerminology?: string;
  speakUpSolution?: string;
} {
  const errors: {
    companyName?: string;
    industry?: string;
    employeeCount?: string;
    regions?: string;
    employeeTerminology?: string;
    speakUpSolution?: string;
  } = {};
  const trimmedName = companyName?.trim() ?? '';

  if (companyName !== null) {
    if (trimmedName.length === 0) {
      errors.companyName = 'required';
    } else if (trimmedName.length > COMPANY_NAME_MAX_LENGTH) {
      errors.companyName = 'max_length';
    }
  }

  const trimmedIndustry = industry.trim();

  if (trimmedIndustry.length === 0) {
    errors.industry = 'required';
  } else if (trimmedIndustry.length > INDUSTRY_MAX_LENGTH) {
    errors.industry = 'max_length';
  }

  if (employeeCount.trim().length === 0) {
    errors.employeeCount = 'required';
  }

  if (regions.length === 0) {
    errors.regions = 'required';
  }

  const employeeTerminologyError = validateOptionalCompanyTerminology(employeeTerminology);
  if (employeeTerminologyError) {
    errors.employeeTerminology = employeeTerminologyError;
  }

  const speakUpSolutionError = validateOptionalCompanyTerminology(speakUpSolution);
  if (speakUpSolutionError) {
    errors.speakUpSolution = speakUpSolutionError;
  }

  return errors;
}

export function isStep3Valid(
  companyName: string | null,
  industry: string,
  employeeCount: string,
  regions: string[],
  employeeTerminology: string,
  speakUpSolution: string,
): boolean {
  return (
    Object.keys(
      validateStep3Fields(
        companyName,
        industry,
        employeeCount,
        regions,
        employeeTerminology,
        speakUpSolution,
      ),
    ).length === 0
  );
}

export function validateStep4Fields(
  trainingTopics: OnboardingTrainingTopic[],
  trainingPackage: string,
): {
  trainingTopics?: string;
} {
  const errors: { trainingTopics?: string } = {};
  const maxTrainingTopics = getOnboardingMaxTrainingTopicsForPackage(trainingPackage);

  // Training priorities are optional — validate count only when topics are selected.
  if (trainingTopics.length > maxTrainingTopics) {
    errors.trainingTopics = 'max_count';
  }

  return errors;
}

export function isStep4Valid(
  trainingTopics: OnboardingTrainingTopic[],
  trainingPackage: string,
): boolean {
  return Object.keys(validateStep4Fields(trainingTopics, trainingPackage)).length === 0;
}

const VALID_MONTH_VALUES = new Set(ONBOARDING_MONTH_OPTIONS.map((month) => month.value));

export function validateStep5Fields(
  priorityMonths: string[],
  monthsToAvoid: string[],
  additionalContext: string,
): {
  priorityMonths?: string;
  monthsToAvoid?: string;
  additionalContext?: string;
} {
  const errors: {
    priorityMonths?: string;
    monthsToAvoid?: string;
    additionalContext?: string;
  } = {};

  if (priorityMonths.length > ONBOARDING_MAX_MONTH_SELECTIONS) {
    errors.priorityMonths = 'max_count';
  } else if (priorityMonths.length > 0 && priorityMonths.some((month) => !VALID_MONTH_VALUES.has(month))) {
    errors.priorityMonths = 'invalid';
  }

  if (monthsToAvoid.length > ONBOARDING_MAX_MONTH_SELECTIONS) {
    errors.monthsToAvoid = 'max_count';
  } else if (monthsToAvoid.length > 0 && monthsToAvoid.some((month) => !VALID_MONTH_VALUES.has(month))) {
    errors.monthsToAvoid = 'invalid';
  }

  const overlap = priorityMonths.filter((month) => monthsToAvoid.includes(month));
  if (overlap.length > 0 && !errors.priorityMonths && !errors.monthsToAvoid) {
    errors.monthsToAvoid = 'overlap';
  }

  // CEE-867: additional notes field removed from onboarding schedule step
  // if (additionalContext.trim().length > ONBOARDING_MAX_ADDITIONAL_CONTEXT_LENGTH) {
  //   errors.additionalContext = 'max_length';
  // }
  void additionalContext;

  return errors;
}

export function isStep5Valid(
  priorityMonths: string[],
  monthsToAvoid: string[],
  additionalContext: string,
): boolean {
  return (
    Object.keys(validateStep5Fields(priorityMonths, monthsToAvoid, additionalContext)).length === 0
  );
}

export function validateStep6Fields(
  brandSkipped: boolean,
  brandGuidelines: boolean | null,
  primaryBrandColor: string,
  secondaryBrandColor: string,
  logoCount: number,
  documentCount: number,
): {
  logos?: string;
  brandGuidelines?: string;
  brandingFiles?: string;
  primaryBrandColor?: string;
  secondaryBrandColor?: string;
} {
  if (brandSkipped) {
    return {};
  }

  const errors: {
    logos?: string;
    brandGuidelines?: string;
    brandingFiles?: string;
    primaryBrandColor?: string;
    secondaryBrandColor?: string;
  } = {};

  if (logoCount === 0) {
    errors.logos = 'required';
  }

  if (brandGuidelines === null) {
    errors.brandGuidelines = 'required';
  } else if (brandGuidelines === true && documentCount === 0) {
    errors.brandingFiles = 'branding_files_required';
  } else if (brandGuidelines === false) {
    const primary = primaryBrandColor.trim();
    const secondary = secondaryBrandColor.trim();
    if (primary === '') {
      errors.primaryBrandColor = 'required';
    } else if (!HEX_COLOR_RE.test(primary)) {
      errors.primaryBrandColor = 'invalid_hex';
    }
    if (secondary === '') {
      errors.secondaryBrandColor = 'required';
    } else if (!HEX_COLOR_RE.test(secondary)) {
      errors.secondaryBrandColor = 'invalid_hex';
    }
  }

  return errors;
}

export function isStep6Valid(
  brandSkipped: boolean,
  brandGuidelines: boolean | null,
  primaryBrandColor: string,
  secondaryBrandColor: string,
  logoCount: number,
  documentCount: number,
): boolean {
  return (
    Object.keys(
      validateStep6Fields(
        brandSkipped,
        brandGuidelines,
        primaryBrandColor,
        secondaryBrandColor,
        logoCount,
        documentCount,
      ),
    ).length === 0
  );
}
